import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { authService } from '../services/auth.service.js';
import { clearInFlightRequests } from '../utils/apiDeduplication.js';

const AuthContext = createContext(null);

// Module-level ref to prevent duplicate auth checks across remounts
let authCheckInitiated = false;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    if (!authCheckInitiated) {
      checkAuth();
      authCheckInitiated = true;
    }
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (token) {
        const userData = await authService.getProfile();
        // userData contains { success: true, data: { user: {...} }, ... }
        setUser(userData.data.user);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Check auth error:', error);
      // Only set to false if there's no token or token is invalid
      // Don't override existing authentication state if tokens exist
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setIsAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const data = await authService.login(email, password);
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    setUser(data.user);
    setIsAuthenticated(true);
    return data;
  };

  const setAuthState = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    console.log('isAuthenticated set to true');
  };

  const logout = async () => {
    // Check if refresh token exists
    const hasRefreshToken = !!localStorage.getItem('refreshToken');

    // Only call logout API if refresh token exists
    if (hasRefreshToken) {
      try {
        await authService.logout();
      } catch (error) {
        console.error('Logout error:', error);
      }
    }

    // Clear any in-flight API requests
    clearInFlightRequests();
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    // Redirect to landing page
    window.location.href = '/';
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    checkAuth,
    setAuthState,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
